import React, { useEffect, useState, useMemo } from "react";
import {
  TextField,
  CircularProgress,
  Grid,
  Button,
  Typography,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormControlLabel,
  Checkbox,
  Box,
  Divider,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useForm, Controller } from "react-hook-form";
import { useParams, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import axios from "axios";
import privilegeConfigData from "../../../config/privileges.json";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

const { PRIVILEGES_CONFIG } = privilegeConfigData;

function RoleEdit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const token = localStorage.getItem("astroVani-admin-token");

  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);

  const {
    register,
    handleSubmit,
    control,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: {
      roleName: "",
      privileges: [],
    },
  });

  const selectedPrivileges = watch("privileges") || [];

  useEffect(() => {
    if (id) {
      const fetchRoleDetails = async () => {
        setInitialLoading(true);
        try {
          const res = await axios.get(`${process.env.REACT_APP_BASEURL}/admin/role/${id}`, {
            headers: { Authorization: token },
          });
          const role = res?.data?.data;

          reset({
            roleName: role?.roleName || "",
            privileges: role?.privileges || [],
          });
        } catch (err) {
          toast.error("Failed to load role details");
        } finally {
          setInitialLoading(false);
        }
      };
      fetchRoleDetails();
    } else {
      setInitialLoading(false);
    }
  }, [id, reset, token]);

  const onSubmit = async (formData) => {
    setLoading(true);
    try {
      if (id) {
        await axios.patch(`${process.env.REACT_APP_BASEURL}/admin/role/${id}`, formData, {
          headers: { Authorization: token },
        });
        toast.success("Role updated successfully!");
      } else {
        await axios.post(`${process.env.REACT_APP_BASEURL}/admin/role`, formData, {
          headers: { Authorization: token },
        });
        toast.success("Role created successfully!");
      }
      navigate("/role-management");
    } catch (err) {
      toast.error(err?.response?.data?.message || "Error while saving role");
    } finally {
      setLoading(false);
    }
  };

  const handleCheckboxChange = (value, checked, moduleObj) => {
    let newPrivileges = new Set(selectedPrivileges);

    if (checked) {
      newPrivileges.add(value);
      // Auto-select 'view' if a sub-action is selected
      if (moduleObj.module !== 'other_routes') {
        const viewPrivilege = moduleObj.privileges.find((p) => p.key === "view");
        if (viewPrivilege) {
          newPrivileges.add(viewPrivilege.value);
        }
      }
    } else {
      newPrivileges.delete(value);
      // If 'view' is unchecked, uncheck all related actions for this module
      if (moduleObj.module !== 'other_routes') {
        const currentPriv = moduleObj.privileges.find((p) => p.value === value);
        if (currentPriv && currentPriv.key === "view") {
          moduleObj.privileges.forEach((p) => newPrivileges.delete(p.value));
        }
      }
    }

    setValue("privileges", Array.from(newPrivileges), { shouldValidate: true });
  };

  const handleSelectAllCategory = (categoryObj, checked) => {
    let newPrivileges = new Set(selectedPrivileges);
    const allPrivilegesInCat = [];

    if (categoryObj.children) {
      categoryObj.children.forEach(child => {
        if (child.privileges) {
          child.privileges.forEach(p => allPrivilegesInCat.push(p.value));
        }
      });
    } else if (categoryObj.privileges) {
      categoryObj.privileges.forEach(p => allPrivilegesInCat.push(p.value));
    }

    if (checked) {
      allPrivilegesInCat.forEach(p => newPrivileges.add(p));
    } else {
      allPrivilegesInCat.forEach(p => newPrivileges.delete(p));
    }

    setValue("privileges", Array.from(newPrivileges), { shouldValidate: true });
  };

  const handleSelectAllModule = (moduleObj, checked) => {
    let newPrivileges = new Set(selectedPrivileges);
    if (!moduleObj.privileges) return;

    if (checked) {
      moduleObj.privileges.forEach(p => newPrivileges.add(p.value));
    } else {
      moduleObj.privileges.forEach(p => newPrivileges.delete(p.value));
    }
    setValue("privileges", Array.from(newPrivileges), { shouldValidate: true });
  };

  const isCategoryFullySelected = (categoryObj) => {
    let allPrivilegesInCat = [];
    if (categoryObj.children) {
      categoryObj.children.forEach(child => {
        if (child.privileges) {
          child.privileges.forEach(p => allPrivilegesInCat.push(p.value));
        }
      });
    } else if (categoryObj.privileges) {
      categoryObj.privileges.forEach(p => allPrivilegesInCat.push(p.value));
    }

    if (allPrivilegesInCat.length === 0) return false;
    return allPrivilegesInCat.every(p => selectedPrivileges.includes(p));
  };

  const isCategoryPartiallySelected = (categoryObj) => {
    let allPrivilegesInCat = [];
    if (categoryObj.children) {
      categoryObj.children.forEach(child => {
        if (child.privileges) {
          child.privileges.forEach(p => allPrivilegesInCat.push(p.value));
        }
      });
    } else if (categoryObj.privileges) {
      categoryObj.privileges.forEach(p => allPrivilegesInCat.push(p.value));
    }

    if (allPrivilegesInCat.length === 0) return false;
    const someSelected = allPrivilegesInCat.some(p => selectedPrivileges.includes(p));
    const allSelected = allPrivilegesInCat.every(p => selectedPrivileges.includes(p));
    return someSelected && !allSelected;
  };

  const isModuleFullySelected = (moduleObj) => {
    if (!moduleObj.privileges || moduleObj.privileges.length === 0) return false;
    return moduleObj.privileges.every(p => selectedPrivileges.includes(p.value));
  };

  const isModulePartiallySelected = (moduleObj) => {
    if (!moduleObj.privileges || moduleObj.privileges.length === 0) return false;
    const someSelected = moduleObj.privileges.some(p => selectedPrivileges.includes(p.value));
    const allSelected = moduleObj.privileges.every(p => selectedPrivileges.includes(p.value));
    return someSelected && !allSelected;
  };

  const renderPrivilegesRow = (moduleObj) => {
    if (!moduleObj.privileges) return null;
    return (
      <Box key={moduleObj.module} sx={{ mb: 1, display: "flex", alignItems: "center", flexWrap: "wrap", p: 1, borderRadius: 1, '&:hover': { bgcolor: '#f5f5f5' } }}>
        <Box sx={{ minWidth: 240, display: "flex", alignItems: "center" }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={isModuleFullySelected(moduleObj)}
                indeterminate={isModulePartiallySelected(moduleObj)}
                onChange={(e) => handleSelectAllModule(moduleObj, e.target.checked)}
                color="secondary"
                size="small"
              />
            }
            label={<Typography variant="body1" sx={{ fontWeight: 600 }}>{moduleObj.label}</Typography>}
          />
        </Box>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, flex: 1, pl: 2, borderLeft: '1px solid #e0e0e0' }}>
          {moduleObj.privileges.map((privilege) => (
            <FormControlLabel
              key={privilege.value}
              control={
                <Checkbox
                  checked={selectedPrivileges.includes(privilege.value)}
                  onChange={(e) => handleCheckboxChange(privilege.value, e.target.checked, moduleObj)}
                  color="primary"
                  size="small"
                />
              }
              label={
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                  <Typography variant="body2">
                    {privilege.label}
                  </Typography>
                  <a
                    href={privilege.value.replace('/:id', '')} // Remove :id for valid navigation if it exists
                    target="_blank"
                    rel='noopener noreferrer'
                    style={{ color: '#888', fontSize: '11px', textDecoration: 'none', marginTop: '2px' }}
                    onClick={(e) => e.stopPropagation()} // Prevent checkbox toggle when clicking the link
                  >
                    ({privilege.value})
                  </a>
                </Box>
              }
            />
          ))}
        </Box>
      </Box>
    );
  };

  if (initialLoading) return <div className="p-4">Loading...</div>;

  return (
    <div style={{ padding: "24px" }}>
      <Paper elevation={3} sx={{ padding: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" gutterBottom sx={{ mb: 0 }}>
            {id ? "Edit Role" : "Create Role"}
          </Typography>
          <Button variant="contained" className="bg-primary" sx={{ p: 0.5 }} onClick={() => navigate(-1)}>
            <ArrowBackIcon sx={{ mr: 1 }} /> Back
          </Button>
        </Box>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={12}>
              <TextField
                fullWidth
                label="Role Name"
                placeholder="e.g. Admin, Manager"
                {...register("roleName", { required: "Role name is required" })}
                error={!!errors.roleName}
                helperText={errors.roleName?.message}
              />
            </Grid>
            <Grid item xs={12} md={12}>
              <Typography variant="h6" sx={{ mt: 2, mb: 2 }}>
                Permissions & Privileges
              </Typography>
              {errors.privileges && (
                <Typography color="error" variant="body2" sx={{ mb: 2 }}>
                  {errors.privileges.message}
                </Typography>
              )}

              <Controller
                name="privileges"
                control={control}
                rules={{ validate: (val) => val.length > 0 || "Select at least one privilege" }}
                render={() => (
                  <Box>
                    {PRIVILEGES_CONFIG.map((category) => {
                      if (!category.children) {
                        return (
                          <Box key={category.module} sx={{ mb: 1, border: '1px solid #e0e0e0', borderRadius: 1, bgcolor: '#f8f9fa' }}>
                            {renderPrivilegesRow(category)}
                          </Box>
                        );
                      }
                      return (
                        <Accordion key={category.module} disableGutters elevation={1} sx={{ mb: 1, border: '1px solid #e0e0e0', '&:before': { display: 'none' } }}>
                          <AccordionSummary
                            expandIcon={<ExpandMoreIcon />}
                            sx={{ bgcolor: "#f8f9fa", borderBottom: '1px solid #e0e0e0' }}
                          >
                            <FormControlLabel
                              onClick={(event) => event.stopPropagation()}
                              onFocus={(event) => event.stopPropagation()}
                              control={
                                <Checkbox
                                  checked={isCategoryFullySelected(category)}
                                  indeterminate={isCategoryPartiallySelected(category)}
                                  onChange={(e) => handleSelectAllCategory(category, e.target.checked)}
                                />
                              }
                              label={<Typography variant="subtitle1" fontWeight="bold">{category.label}</Typography>}
                            />
                          </AccordionSummary>
                          <AccordionDetails sx={{ p: 3 }}>
                            {category.children.map((child, index) => (
                              <React.Fragment key={child.module}>
                                {renderPrivilegesRow(child)}
                                {index < category.children.length - 1 && <Divider sx={{ my: 1 }} />}
                              </React.Fragment>
                            ))}
                          </AccordionDetails>
                        </Accordion>
                      );
                    })}
                  </Box>
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={loading}
                sx={{ minWidth: 140, mt: 2 }}
              >
                {loading ? <CircularProgress size={20} color="inherit" /> : id ? "Update Role" : "Create Role"}
              </Button>
            </Grid>
          </Grid>
        </form>
      </Paper>
    </div>
  );
}

export default RoleEdit;